import { useEffect, useState } from "react";

const THEME_STORAGE_KEY = "theme";
const THEME_LIGHT = "light";
const THEME_DARK = "dark";
const THEME_SYSTEM = "system";

const getSystemPrefersDark = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const getStoredThemeMode = () => {
  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  if (savedTheme === THEME_LIGHT || savedTheme === THEME_DARK || savedTheme === THEME_SYSTEM) {
    return savedTheme;
  }
  return THEME_SYSTEM;
};

/**
 * Custom hook for managing dark mode
 * Syncs with localStorage and applies data-theme attribute to HTML element
 */
export const useDarkMode = () => {
  const [themeMode, setThemeModeState] = useState(getStoredThemeMode);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const initialMode = getStoredThemeMode();
    return initialMode === THEME_SYSTEM ? getSystemPrefersDark() : initialMode === THEME_DARK;
  });

  useEffect(() => {
    const root = document.documentElement;
    const nextIsDark =
      themeMode === THEME_SYSTEM ? getSystemPrefersDark() : themeMode === THEME_DARK;
    setIsDarkMode(nextIsDark);
    root.setAttribute("data-theme", nextIsDark ? THEME_DARK : THEME_LIGHT);
    localStorage.setItem(THEME_STORAGE_KEY, themeMode);
  }, [themeMode]);

  // Listen for system theme changes
  useEffect(() => {
    if (themeMode !== THEME_SYSTEM || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e) => {
      setIsDarkMode(e.matches);
      document.documentElement.setAttribute("data-theme", e.matches ? THEME_DARK : THEME_LIGHT);
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
    return undefined;
  }, [themeMode]);

  // Listen for localStorage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === THEME_STORAGE_KEY && e.newValue) {
        const newTheme = e.newValue;
        if (newTheme === THEME_LIGHT || newTheme === THEME_DARK || newTheme === THEME_SYSTEM) {
          setThemeModeState(newTheme);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Listen for theme-sync event from App.jsx (same tab, backend sync)
  useEffect(() => {
    const handleThemeSync = (e) => {
      const newTheme = e.detail;
      if (newTheme === THEME_LIGHT || newTheme === THEME_DARK || newTheme === THEME_SYSTEM) {
        setThemeModeState(newTheme);
      }
    };

    window.addEventListener("theme-sync", handleThemeSync);
    return () => window.removeEventListener("theme-sync", handleThemeSync);
  }, []);

  const toggleDarkMode = () => {
    setThemeModeState((prev) => (prev === THEME_DARK ? THEME_LIGHT : THEME_DARK));
  };

  const setDarkMode = (value) => {
    setThemeModeState(value ? THEME_DARK : THEME_LIGHT);
  };

  const setThemeMode = (mode) => {
    if (mode === THEME_LIGHT || mode === THEME_DARK || mode === THEME_SYSTEM) {
      setThemeModeState(mode);
    }
  };

  return {
    isDarkMode,
    themeMode,
    toggleDarkMode,
    setDarkMode,
    setThemeMode,
  };
};
